import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, ArrowUpDown, Loader2, Bot } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminApi } from "@/lib/adminApi";
import { StatusBadge } from "@/components/admin/_shared";
import AdminCustomerActions from "@/components/admin/AdminCustomerActions";

export default function AdminCustomers() {
  const navigate = useNavigate();
  const [data, setData] = useState({ customers: [], total: 0 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const pageSize = 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/customers", { params: { search, status, sort, order, page, page_size: pageSize } });
      setData(r.data);
    } finally { setLoading(false); }
  }, [search, status, sort, order, page]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggleSort = (field) => {
    if (sort === field) setOrder(order === "asc" ? "desc" : "asc");
    else { setSort(field); setOrder("asc"); }
    setPage(1);
  };
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  const cols = [
    ["business_name", "Business"], ["owner", "Owner"], ["email", "Email"],
    ["industry", "Industry"], ["plan", "Plan"], ["status", "Subscription"],
    ["ai_live", "AI"], ["minutes_used", "Minutes"], ["created_at", "Registered"],
  ];

  return (
    <div data-testid="admin-customers">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Customer Management</h1>
      <div className="mt-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="customer-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search business, owner, email…" className="pl-9" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger data-testid="customer-status-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="trialing">Trialing</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="none">No subscription</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                {cols.map(([field, label]) => (
                  <th key={field} className="text-left font-semibold px-4 py-3 whitespace-nowrap">
                    <button onClick={() => toggleSort(field)} className="inline-flex items-center gap-1 hover:text-slate-800" data-testid={`sort-${field}`}>{label} <ArrowUpDown className="h-3 w-3" /></button>
                  </th>
                ))}
                <th className="text-right font-semibold px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={cols.length + 1} className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></td></tr>}
              {!loading && data.customers.length === 0 && <tr><td colSpan={cols.length + 1} className="py-12 text-center text-slate-400">No customers found</td></tr>}
              {!loading && data.customers.map((c) => (
                <tr key={c.user_id} data-testid={`customer-row-${c.user_id}`} onClick={() => navigate(`/admin/customers/${c.user_id}`)} className="hover:bg-blue-50/40 cursor-pointer">
                  <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">{c.business_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{c.owner}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{c.email}</td>
                  <td className="px-4 py-3 whitespace-nowrap capitalize">{c.industry}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{c.plan || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3">{c.ai_live ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><Bot className="h-3.5 w-3.5" /> Live</span> : <span className="text-xs text-slate-400">{c.has_assistant ? "Test" : "—"}</span>}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{c.minutes_used}{c.minutes_limit ? `/${c.minutes_limit}` : ""}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}><AdminCustomerActions customer={c} onDone={load} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <p className="text-xs text-slate-500">{data.total} customers · page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button data-testid="prev-page" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button data-testid="next-page" variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
